import React, { useEffect, useMemo, useState } from "react";
import { HbrInput, HbrTextarea, HbrSelect, HbrOption } from "@harbor/elements-react-standalone";
import {
  FormComponentChange,
  FormComponentType,
  FormErrorType,
  useTestCaseFormContext,
} from "./ValidationTestCaseFormContext";
import FormSectionTestCase from "./FormSectionTestCase";
import { useModal } from "../../../../security-toolbox-ui/StbModalContext";
import StbButton from "../../../../security-toolbox-ui/StbButton";
import StbAccordion from "../../../../security-toolbox-ui/StbAccordion";
import { useParams } from "react-router-dom";
import {
  useFormAddPovUseCaseMutation,
  useFormDeletePovUseCaseMutation,
  useFormUpdatePovUseCaseMutation,
  useFormAddPovTestCaseMutation,
} from "../../../../../hooks/opportunity-pov-hooks";
import { UseCase, TestCase } from "../../../../../types/opportunity-pov-items";
import { asSingleValue, unicodeToPlain } from "../../../../../support/utils";
import StbModal from "../../../../security-toolbox-ui/StbModal";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 1000;
const OTHER_OPTION_VALUE = "__other__";

type ProductFeatureSuccessCriteriaOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  success_criteria?: string;
  test_plan?: string;
};

type ProductFeatureOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  product_feature_success_criteria?: ProductFeatureSuccessCriteriaOption[];
};

type ProductUseCaseOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  product_feature?: ProductFeatureOption[];
};

export const FormSectionUseCase: React.FC<{
  formSectionId: string;
}> = ({ formSectionId }) => {
  const { id: opportunityId } = useParams();
  const { openModal, closeModal } = useModal();
  const {
    getFormItem,
    setFormItemSaved,
    updateFormItem,
    addFormItem,
    addFormItemWithData,
    removeFormItem,
    setFormItem,
    setFormError,
    replaceChildren,
    opportunityProduct,
  } = useTestCaseFormContext();

  const usecase = useMemo(() => getFormItem(formSectionId), [getFormItem, formSectionId]);

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    if (usecase?.change === FormComponentChange.Added) {
      setIsEditing(true);
      setIsOpen(true);
    }
  }, [usecase?.change]);

  const prev = useMemo(() => (isEditing ? usecase?.data : undefined), [isEditing, usecase?.data]);

  const { mutateAsync: createUseCase } = useFormAddPovUseCaseMutation(opportunityId);
  const { mutateAsync: updateUseCase } = useFormUpdatePovUseCaseMutation(opportunityId);
  const { mutateAsync: deleteUseCase } = useFormDeletePovUseCaseMutation(opportunityId);
  const { mutateAsync: createTestCase } = useFormAddPovTestCaseMutation(opportunityId);

  const productWithCatalog = opportunityProduct?.product as any;

  const productUseCases: ProductUseCaseOption[] = useMemo(() => {
    return (
      productWithCatalog?.use_case ||
      productWithCatalog?.product_use_case ||
      productWithCatalog?.product_use_cases ||
      []
    );
  }, [productWithCatalog]);

  const selectedCatalogUseCase = useMemo(() => {
    const selectedId = usecase?.data?.product_use_case;
    if (!selectedId) return undefined;
    return productUseCases.find((item) => item.id === selectedId);
  }, [productUseCases, usecase?.data?.product_use_case]);

  const selectedOptionValue = useMemo(() => {
    if (usecase?.data?.is_custom) {
      return OTHER_OPTION_VALUE;
    }
    if (usecase?.data?.product_use_case) {
      return String(usecase.data.product_use_case);
    }
    return "";
  }, [usecase?.data?.is_custom, usecase?.data?.product_use_case]);

  const handleInputChange = (field: string, value: string) => {
    updateFormItem(formSectionId, field, value);
    if (usecase?.error) {
      setFormError(formSectionId, "");
    }
  };

  const rebuildTestCasesFromCatalogUseCase = (catalogUseCase?: ProductUseCaseOption) => {
    const existingChildren = usecase?.children || [];
    if (existingChildren.length > 0) {
      removeFormItem(existingChildren);
    }

    if (!catalogUseCase) {
      replaceChildren(formSectionId, []);
      return;
    }

    const features = catalogUseCase.product_feature || [];
    const newChildIds: string[] = [];

    features.forEach((feature) => {
      const criteria = feature.product_feature_success_criteria || [];

      criteria.forEach((criterion) => {
        const childId = addFormItemWithData(
          FormComponentType.TestCase,
          {
            title: criterion.success_criteria || criterion.title || criterion.name || "",
            description: criterion.test_plan || criterion.description || "",
            product_feature_success_criteria: criterion.id,
            is_custom: false,
            feature_name: feature.name || feature.title || "",
            feature_id: feature.id,
          },
          formSectionId
        );
        newChildIds.push(childId);
      });
    });

    replaceChildren(formSectionId, newChildIds);
  };

  const handleCatalogSelectionChange = (value: string) => {
    if (value === OTHER_OPTION_VALUE) {
      updateFormItem(formSectionId, "is_custom", true);
      updateFormItem(formSectionId, "product_use_case", null);
      updateFormItem(formSectionId, "title", "");
      updateFormItem(formSectionId, "description", "");

      const existingChildren = usecase?.children || [];
      if (existingChildren.length > 0) {
        removeFormItem(existingChildren);
        replaceChildren(formSectionId, []);
      }

      if (usecase?.error) {
        setFormError(formSectionId, "");
      }
      return;
    }

    const selectedId = Number(value);
    const catalogUseCase = productUseCases.find((item) => item.id === selectedId);
    if (!catalogUseCase) {
      return;
    }

    updateFormItem(formSectionId, "is_custom", false);
    updateFormItem(formSectionId, "product_use_case", catalogUseCase.id);
    updateFormItem(formSectionId, "title", (catalogUseCase.title || catalogUseCase.name || "").trim());
    updateFormItem(formSectionId, "description", (catalogUseCase.description || "").trim());

    rebuildTestCasesFromCatalogUseCase(catalogUseCase);

    if (usecase?.error) {
      setFormError(formSectionId, "");
    }
  };

  const handleDeleteUseCase = async () => {
    const testcases = usecase?.children || [];
    try {
      await deleteUseCase(usecase?.data as UseCase);
      removeFormItem([...testcases, formSectionId]);
    } catch (error) {
      setFormError(formSectionId, "Failed to delete use case", FormErrorType.Api);
      console.error("Error deleting use case:", error);
    }
  };

  const handleOpenDeleteConfirmModal = () => {
    openModal({
      content: () => (
        <StbModal
          open={true}
          role="delete"
          onSubmit={handleDeleteUseCase}
          onClose={async () => closeModal()}
        >
          <div>
            Are you sure you want to delete this use case
            {usecase?.children?.length > 0 ? " and all child test cases?" : "?"}
          </div>
        </StbModal>
      ),
    });
  };

  const handleCancelClick = () => {
    if (usecase?.change === FormComponentChange.Added) {
      removeFormItem(formSectionId);
    } else {
      setIsEditing(false);
      setFormItem(formSectionId, prev);
    }
    setFormError(formSectionId, undefined, undefined);
  };

  const handleAddTestCaseButtonClick = () => {
    addFormItem(FormComponentType.TestCase, usecase?.formSectionId);
  };

  const handleUpdateUseCaseClick = () => {
    setIsEditing(true);
    setIsOpen(true);
  };

  const persistStagedChildrenForNewUseCase = async (savedUseCaseId: number) => {
    const childIds = usecase?.children || [];

    for (const childId of childIds) {
      const child = getFormItem(childId);
      if (!child?.data) continue;

      if (child.change !== FormComponentChange.Added) continue;

      const childPayload = {
        ...child.data,
        pov_usecase: savedUseCaseId,
        product_feature_success_criteria: child.data.product_feature_success_criteria ?? null,
        is_custom: child.data.is_custom ?? true,
      } as TestCase;

      const savedChild = await createTestCase(childPayload);

      setFormItemSaved(childId, {
        ...savedChild,
        feature_name: child.data.feature_name,
        feature_id: child.data.feature_id,
      });
    }
  };

  const handleSaveClick = async () => {
    const localUseCase = (usecase?.data || {}) as UseCase;
    const isCustom = localUseCase.is_custom ?? !localUseCase.product_use_case;
    const hasCatalogSelection = !!localUseCase.product_use_case;
    const trimmedTitle = (localUseCase.title || "").trim();
    const trimmedDescription = (localUseCase.description || "").trim();

    if (!isCustom && !hasCatalogSelection) {
      setFormError(formSectionId, "Use case selection is required", FormErrorType.Form);
      return;
    }

    if (isCustom && !trimmedTitle) {
      setFormError(formSectionId, "Title is required", FormErrorType.Form);
      return;
    }

    if (usecase?.change === FormComponentChange.NoChange) {
      setIsEditing(false);
      return;
    }

    try {
      const outcome = getFormItem(usecase?.parent)?.data?.id;
      const payload = {
        ...localUseCase,
        title: trimmedTitle,
        description: trimmedDescription,
        pov_outcome: +outcome,
        type: "Use Case",
        product: opportunityProduct?.product?.id,
        product_use_case: localUseCase.product_use_case ?? null,
        is_custom: isCustom,
      } as UseCase;

      let result;
      if (usecase?.change === FormComponentChange.Added) {
        result = await createUseCase(payload as any);
        setFormItemSaved(formSectionId, result);

        if (!isCustom && result?.id) {
          await persistStagedChildrenForNewUseCase(result.id);
        }
      } else if (usecase?.change === FormComponentChange.Updated) {
        result = await updateUseCase(payload as any);
        setFormItemSaved(formSectionId, result);
      }

      setIsEditing(false);
      setFormError(formSectionId, undefined, undefined);
    } catch (error) {
      setFormError(formSectionId, "Failed to save use case", FormErrorType.Api);
      console.error("Error saving use case:", error);
    }
  };

  return (
    <StbAccordion
      isOpen={isOpen}
      toggleIsOpen={setIsOpen}
      interactive={!isEditing}
      variant="stacked"
      className="w-full"
      headerContent={
        <div className="flex items-start justify-between gap-4 w-full">
          <div className="text-base font-semibold break-words flex-1 min-w-0">
            {usecase?.data?.title || "New Use Case"}
          </div>
          <div className="flex flex-shrink-0 items-center">
            {!isEditing && (
              <>
                <StbButton
                  iconOnly={true}
                  role="update"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateUseCaseClick();
                  }}
                />
                <StbButton
                  iconOnly={true}
                  role="delete"
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteConfirmModal();
                  }}
                />
              </>
            )}
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 py-3">
        <div className="min-w-0">
          {isEditing && (
            <div className="flex flex-col gap-3 mb-4">
              <HbrSelect
                value={selectedOptionValue}
                placeholder="Select use case"
                onHbr-change={(e) => {
                  handleCatalogSelectionChange(asSingleValue(e.target.value));
                }}
              >
                <div slot="label">Use Case</div>
                {productUseCases.map((item) => (
                  <HbrOption key={item.id} value={String(item.id)}>
                    {item.title || item.name}
                  </HbrOption>
                ))}
                <HbrOption value={OTHER_OPTION_VALUE}>Other</HbrOption>
              </HbrSelect>

              {usecase?.data?.is_custom && (
                <>
                  <HbrInput
                    value={usecase?.data?.title}
                    placeholder="Use case title"
                    maxlength={MAX_TITLE_LENGTH}
                    onHbr-input={(e) => {
                      handleInputChange("title", unicodeToPlain(e.target.value));
                    }}
                    error={usecase?.error?.length > 0}
                  >
                    <div slot="label">Name</div>
                    {usecase?.error && usecase?.errorType === FormErrorType.Form && (
                      <div slot="help-text" style={{ color: "var(--hbr-color-danger)" }}>
                        {usecase?.error}
                      </div>
                    )}
                  </HbrInput>

                  <HbrTextarea
                    className="w-full"
                    value={usecase?.data?.description}
                    placeholder="Add use case description"
                    maxlength={MAX_DESCRIPTION_LENGTH}
                    onHbr-input={(e) => handleInputChange("description", unicodeToPlain(e.target.value))}
                  />
                </>
              )}

              {!usecase?.data?.is_custom && selectedCatalogUseCase?.description ? (
                <div className="text-xs text-gray-600">{selectedCatalogUseCase.description}</div>
              ) : null}

              <div className="mt-2">
                <StbButton
                  iconOnly={true}
                  role="save"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveClick();
                  }}
                />
                <StbButton
                  iconOnly={true}
                  role="cancel"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelClick();
                  }}
                />
              </div>
            </div>
          )}

          {!isEditing && usecase?.data?.description?.length > 0 ? (
            <div className="text-sm">{usecase?.data?.description}</div>
          ) : !isEditing ? (
            <div className="italic text-sm">No description</div>
          ) : null}

          {usecase?.error && usecase?.errorType === FormErrorType.Api && (
            <div style={{ color: "var(--hbr-color-danger)" }}>{usecase?.error}</div>
          )}
        </div>

        <div className="min-w-0">
          {usecase?.children?.length > 0 ? (
            usecase.children.map((id) => <FormSectionTestCase formSectionId={id} key={id} />)
          ) : (
            <div className="text-sm font-normal italic">No test cases found</div>
          )}

          {![FormComponentChange.Added, FormComponentChange.Deleted].includes(usecase?.change) && (
            <StbButton
              role="add"
              size="medium"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                handleAddTestCaseButtonClick();
              }}
              className="mt-2"
            >
              Add Test Case
            </StbButton>
          )}
        </div>
      </div>
    </StbAccordion>
  );
};

export default FormSectionUseCase;